import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import {
    isBetslipEmpty,
    isBetslipOpen,
    isBetslipPlacementOpen,
    getBetslipSelectionIds,
    onSelectionAddedToBetslip,
    onSelectionsRemovedFromBetslip,
    onBetslipClosed,
    getBetslipSelectionsDomElement,
    onBeforeBetslipSync,
    onBetslipPlacementShown,
    onBetslipOpened,
} from '../../shared/utils/betslip';
import { isLargeContainer } from '../../spa/Utils/ContainerSize';
import {
    BETSLIP_PLACEMENT_RECOMMENDER_CSS_ID,
    BETSLIP_SELECTIONS_HAS_RECOMMENDATIONS_CLASS_NAME,
    RECOMMENDATION_BETSLIP_PLACEMENT_GA_COMPONENT_NAME,
    RECOMMENDATION_LOCATION_BETSLIP_NAME,
} from './constants';
import {
    addRecommendedFlagToBetslipSelection,
    getRecommendSelectionsDelay,
} from './utils';
import createRecommendationsView from '../../shared/components/recommendations-view/createRecommendationsView';
import {
    getSelectionRecommendations,
} from '../../shared/services/recommendations';
import { SELECTION_VIEWS_SHOWN_EVENT } from '../../shared/components/recommendations-view/constants';
import { setAsGaComponent } from '../../shared/utils/google-analytics';

/**
 * The betslip placement recommendations
 * @return {void}
 */
export function initilaizeBetslipPlacementRecommendations() {
    let eventListeners = [];

    /**
     * The recommendations view
     * @type {RecommendationsView}
     */
    const recommendationsView = createRecommendationsView();

    /**
     * Flag to track the change state of the betslip selections
     * @type {Boolean}
     */
    let betslipSelectionsHasChanged = !isEmpty(getBetslipSelectionIds());

    /**
     * The last removed selection ids.
     * This is used since the betslip does remove a selection
     * until it is done syncing with the backend
     * @type {Array}
     * @todo Since OR-884 we now use the 'betslip:selections:removed' this
     * function should be safe as the event is only emitted after the sync.
     */
    let lastRemovedSelectionIds = [];

    /**
     * Recommend selections when a user adds selections to the betslip
     * @return {void}
     */
    const recommendBetslipPlacementSelections = debounce(() => {
        const container = document.getElementById(BETSLIP_PLACEMENT_RECOMMENDER_CSS_ID);

        const selectionIdsToRemove = lastRemovedSelectionIds;
        lastRemovedSelectionIds = [];

        if (container === null) {
            return;
        }

        if (!container.contains(recommendationsView.el)) {
            container.appendChild(recommendationsView.el);
        }

        if (isBetslipEmpty()) {
            recommendationsView.clear();

            return;
        }

        if (
            isBetslipOpen() &&
            isBetslipPlacementOpen() &&
            (betslipSelectionsHasChanged || recommendationsView.isHidden())
        ) {

            const selectionChoices = getBetslipSelectionIds().filter(id => {
                return !selectionIdsToRemove.includes(String(id));
            });

            if (selectionChoices.length) {
                getSelectionRecommendations(selectionChoices, RECOMMENDATION_LOCATION_BETSLIP_NAME)
                    .then(recommendationsView.setSelections)
                    .catch(recommendationsView.clear);
            }
        }
    }, getRecommendSelectionsDelay());

    recommendationsView.ev.on(SELECTION_VIEWS_SHOWN_EVENT, () => {
        const selectionsListsEl = getBetslipSelectionsDomElement();

        betslipSelectionsHasChanged = false;

        if (selectionsListsEl !== null) {

            if (recommendationsView.isEmpty()) {
                selectionsListsEl.classList.remove(
                    BETSLIP_SELECTIONS_HAS_RECOMMENDATIONS_CLASS_NAME
                );
            } else {
                selectionsListsEl.classList.add(
                    BETSLIP_SELECTIONS_HAS_RECOMMENDATIONS_CLASS_NAME
                );
            }
        }
    });

    /**
     * Recommend selections on initialization
     * but only when betslip is not empty.
     */
    if (betslipSelectionsHasChanged) {
        recommendBetslipPlacementSelections();
    }

    const onSelectionAddedToBetslipEvent = onSelectionAddedToBetslip(({ id }) => {
        if (recommendationsView.hasSelection(id)) {
            return;
        }

        betslipSelectionsHasChanged = true;
        recommendBetslipPlacementSelections();
    });
    eventListeners.push(onSelectionAddedToBetslipEvent);

    const onSelectionsRemovedFromBetslipEvent = onSelectionsRemovedFromBetslip(({ selectionIds }) => {
        selectionIds.forEach(id => {
            if (recommendationsView.hasSelection(id)) {
                return;
            }

            lastRemovedSelectionIds.push(id);
            betslipSelectionsHasChanged = true;
        });

        if ( betslipSelectionsHasChanged ) {
            recommendBetslipPlacementSelections();
        }
    });
    eventListeners.push(onSelectionsRemovedFromBetslipEvent);

    const onBetslipPlacementShownEvent = onBetslipPlacementShown(recommendBetslipPlacementSelections);
    eventListeners.push(onBetslipPlacementShownEvent);

    const onBetslipOpenedEvent = onBetslipOpened(recommendBetslipPlacementSelections);
    eventListeners.push(onBetslipOpenedEvent);

    const onBetslipClosedEvent = onBetslipClosed(() => {
        if (!isBetslipEmpty()) {
            recommendationsView.clear();
        }
    });
    eventListeners.push(onBetslipClosedEvent);

    const onBeforeBetslipSyncEvent = onBeforeBetslipSync(({ userBetslip }) => {
        if (isLargeContainer() && isBetslipPlacementOpen()) {
            addRecommendedFlagToBetslipSelection({ userBetslip, recommendationsView});
        }
    });
    eventListeners.push(onBeforeBetslipSyncEvent);

    setAsGaComponent(recommendationsView.el, RECOMMENDATION_BETSLIP_PLACEMENT_GA_COMPONENT_NAME);

    const destroy = () => {
        eventListeners.forEach(
            (fn) => fn(
                (unsubscribe) => unsubscribe()
            )
        );
        recommendationsView.clear();
    };

    return {
        destroy,
    };
}

